import React from "react";
import styled, { css } from "styled-components";

import { Icons, IThemeProps } from "@/styles";
import Svg from "@/styles/Svg";

type FormInputProps = {
  name?: string;
  label: string;
  required?: { active: boolean; text: string };
  disabled?: boolean;
  defaultValue?: string;
  value: string;
  valid?: boolean;
  onChange: (value: string) => void;
};

class FormInput extends React.PureComponent<FormInputProps> {
  public static defaultProps: FormInputProps = {
    label: "",
    disabled: false,
    value: "",
    onChange: () => null,
  };

  public render(): JSX.Element {
    const {
      name,
      label,
      disabled,
      defaultValue,
      required,
      valid,
      value,
      onChange,
    } = this.props;

    return (
      <InputWrapper required={required}>
        {!valid && (
          <InputStatus>
            <Svg contents={Icons.Warning} height={18} width={18} />
          </InputStatus>
        )}
        <Input
          data-qa={`input.${name}`}
          className={valid ? "" : "invalid"}
          id={name}
          name={name}
          disabled={disabled}
          defaultValue={defaultValue}
          valid={valid}
          value={value}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onChange(event.target.value)
          }
        />
        <InputLabel hasValue={!!value}>{label}</InputLabel>
      </InputWrapper>
    );
  }
}

export default FormInput;

const InputLabel = styled.span<{ hasValue: boolean }>`
  position: absolute;
  top: ${(props: any) =>
    props.hasValue ? 12 : props.theme.spacing.desktop.s600}px;
  left: 0;
  padding-left: ${(props: any) => props.theme.spacing.desktop.s500}px;
  width: auto;
  font-size: ${(props: any) =>
    props.hasValue ? props.theme.typography.h100 : props.theme.typography.h300};
  color: ${(props: any) =>
    props.theme.colors.inputColor
      ? props.theme.colors.inputColor
      : props.theme.colors.primary};
  transition: top 0.15s linear;
  cursor: text;
  text-transform: uppercase;

  ${(props: any) =>
    props.theme.globals.isTouch === false &&
    `&:focus, &:hover {
        top: 12px;
        font-size: ${props.theme.typography.h100};
    }`}
`;

const InputStatus = styled.span`
  position: absolute;
  bottom: 15px;
  right: 10px;
  svg {
    fill: ${(props: any) => props.theme.colors.error};
  }
`;

const ErrorInput = css`
  border-color: ${(props: any) => props.theme.colors.error};

  ${(props: any) =>
    props.theme.globals.isTouch === false &&
    `&:hover {
                border-color: ${props.theme.colors.error};
            }
            &:focus,
            &:active {
                border-bottom: 2px solid ${props.theme.colors.error};
            }`}
`;

export const InputWrapper = styled.label<any>`
  flex: 1 1 auto;
  position: relative;
  width: auto;
  height: 56px;
  margin-bottom: ${(props: any) => props.theme.spacing.desktop.s700}px;

  ${(props: any) =>
    props.required &&
    `&:after {
            content: "* ${props.required.text}";
            position: absolute;
            bottom: -16px;
            left: 10px;
            font-size: 11px;
            color: ${
              props.theme.colors.inputColor
                ? props.theme.colors.inputColor
                : props.theme.colors.primary
            };
        }`}
`;

type InputProps = IThemeProps<{
  valid?: boolean;
}>;
export const Input = styled.input.attrs({ type: "text" })<InputProps>`
  font-size: ${(props: InputProps) => props.theme.typography.h400};
  @media ${(props: InputProps) => props.theme.breakpoints.mobile} {
    font-size: 16px;
  }
  width: 100%;
  height: 56px;
  padding-top: ${(props: InputProps) => props.theme.spacing.desktop.s300}px;
  padding-right: ${(props: InputProps) =>
    props.theme.spacing.desktop.s300 * 2.5}px;
  padding-left: ${(props: InputProps) => props.theme.spacing.desktop.s500}px;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.83;
  letter-spacing: normal;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-bottom: 1px solid
    ${(props: InputProps) => props.theme.colors.borderGrey};
  background-color: ${(props: InputProps) => props.theme.colors.lightGrey};
  transition: all 0.2s ease-in;
  &:hover {
    background-color: rgba(33, 63, 89, 0.2);
    border-color: ${(props: InputProps) => props.theme.colors.primary};
  }
  &:focus,
  &:active {
    background-color: rgba(33, 63, 89, 0.2);
    border-bottom: 2px solid
      ${(props: InputProps) => props.theme.colors.primary};
  }
  &:focus ~ span,
  &:hover ~ span,
  &:active ~ span {
    top: 12px;
    font-size: ${(props: InputProps) => props.theme.typography.h100};
  }
  ${(props: InputProps) => !props.valid && ErrorInput}
`;
